import { useCallback, useEffect, useRef, useState } from 'react'

import {
  Anchor,
  Box,
  Group,
  InputWrapper,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconAntenna } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { useAdminFund } from '@repo/common/queries/admin/fund'
import { useCreateFundDepositTransaction } from '@repo/common/queries/admin/fund/mutation_create_fund_deposit_transaction.ts'

import { TowerCard } from '@/components/ui/TowerCard'
import { TowerDateTimeInput } from '@/components/ui/TowerDateTimeInput'

import { TowerApprovalControl } from '../ui/TowerApprovalControl'

interface DepositRecordingFormProps {
  token: string
  fundId: string
}

export function DepositRecordingForm({
  fundId,
  token,
}: DepositRecordingFormProps) {
  const [usdTotal, setUsdTotal] = useState<number | string>(0)
  const [destinationAddress, setDestinationAddress] = useState<string>('')
  const [externalTx, setExternalTx] = useState<string>('')
  const [externalType, setExternalType] = useState<string>('')
  const [executedAt, setExecutedAt] = useState<string | null>(null)
  const [showTimestampSection, setShowTimestampSection] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [locked, setLocked] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmationInput, setConfirmationInput] = useState<string>('')
  const navigate = useNavigate()
  const submittingRef = useRef(false)
  const modalCompletionRef = useRef<((success: boolean) => void) | null>(null)

  const { data: fund } = useAdminFund(fundId)
  const createDepositTransaction = useCreateFundDepositTransaction()
  const feeder = fund?.feeders?.find((f) => f.symbol === token)
  const inputExecutedAt = !fund?.feeders?.find((x) => x.tradable)

  const isValidEthereumAddress = (address: string): boolean => {
    const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/
    return ethAddressRegex.test(address)
  }

  const currentPricePerShare = fund?.live_nav?.price_per_share || 0

  const estimatedShareCount =
    typeof usdTotal === 'number' && usdTotal > 0 && currentPricePerShare > 0
      ? usdTotal / currentPricePerShare
      : 0

  const handleInitialSubmit = (): Promise<void> => {
    setFormError(null)

    if (typeof usdTotal !== 'number' || usdTotal <= 0) {
      setFormError('USD total must be a positive number')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (!destinationAddress.trim()) {
      setFormError('Destination address is required')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (!isValidEthereumAddress(destinationAddress.trim())) {
      setFormError('Please enter a valid Ethereum address')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (!externalTx.trim()) {
      setFormError('External transaction ID is required')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (!externalType.trim()) {
      setFormError('External transaction type is required')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (inputExecutedAt && showTimestampSection && !executedAt) {
      setFormError('Execution timestamp is required')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    if (!feeder) {
      setFormError('Invalid feeder token. Please check the URL.')
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject()
    }

    setShowConfirmModal(true)

    return new Promise<void>((resolve, reject) => {
      modalCompletionRef.current = (success: boolean) => {
        if (success) {
          resolve()
        } else {
          reject(new Error('Transaction cancelled or failed'))
        }
      }
    })
  }

  const handleFinalSubmit = useCallback(async (): Promise<void> => {
    if (submittingRef.current) {
      return
    }

    if (!feeder || typeof usdTotal !== 'number') {
      setFormError('Invalid form state')
      setShowConfirmModal(false)
      modalCompletionRef.current?.(false)
      return
    }

    submittingRef.current = true

    try {
      const result = await createDepositTransaction.mutateAsync({
        feeder_id: feeder.id,
        amount: usdTotal,
        user_wallet_address: destinationAddress.trim(),
        action_external_tx: externalTx.trim(),
        action_external_type: externalType as
          | 'wire_transfer'
          | 'ach'
          | 'sepa'
          | 'cash',
        ...(inputExecutedAt && executedAt ? { executed_at: executedAt } : {}),
      })

      if (result.errors.length > 0) {
        setFormError(result.errors.join(', '))
        setShowConfirmModal(false)
        submittingRef.current = false
        modalCompletionRef.current?.(false)
        return
      }

      setShowConfirmModal(false)
      modalCompletionRef.current?.(true)

      setTimeout(() => {
        void navigate({ to: '/fund/$id', params: { id: fundId } })
      }, 2000)
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'An error occurred')
      setShowConfirmModal(false)
      submittingRef.current = false
      modalCompletionRef.current?.(false)
    }
  }, [
    createDepositTransaction,
    destinationAddress,
    executedAt,
    externalTx,
    externalType,
    feeder,
    fundId,
    inputExecutedAt,
    navigate,
    usdTotal,
  ])

  const handleConfirmModalClose = (): void => {
    setShowConfirmModal(false)
    setConfirmationInput('')
    submittingRef.current = false
    modalCompletionRef.current?.(false)
  }

  const getLastFourChars = useCallback((): string => {
    return destinationAddress.slice(-4).toLowerCase()
  }, [destinationAddress])

  useEffect(() => {
    if (
      confirmationInput.length === 4 &&
      confirmationInput.toLowerCase() === getLastFourChars() &&
      !submittingRef.current
    ) {
      setConfirmationInput('')
      void handleFinalSubmit()
    }
  }, [confirmationInput, getLastFourChars, handleFinalSubmit])

  const handleStartApprove = (): void => {
    setLocked(true)
  }

  const handleCancelApprove = (): void => {
    setLocked(false)
  }

  const resetToDefaultTimestamp = (): void => {
    setExecutedAt(null)
    setShowTimestampSection(false)
  }

  const isFormValid =
    typeof usdTotal === 'number' &&
    usdTotal > 0 &&
    destinationAddress.trim() &&
    isValidEthereumAddress(destinationAddress.trim()) &&
    externalTx.trim() &&
    externalType.trim() &&
    (!inputExecutedAt || !showTimestampSection || executedAt) &&
    Boolean(feeder)

  return (
    <TowerCard
      title="Record Deposit Transaction"
      description="Records a deposit of funds on behalf of a buyer"
      icon={<IconAntenna size={24} />}
    >
      <Stack>
        <NumberInput
          label="USD Total"
          value={usdTotal}
          onChange={setUsdTotal}
          required
          decimalScale={2}
          size="md"
          min={0.01}
          disabled={locked}
          error={
            typeof usdTotal === 'number' && usdTotal <= 0
              ? 'USD total must be positive'
              : ''
          }
          description={
            <Text fw={500} size="sm">
              Total USD amount for this mint transaction
            </Text>
          }
        />

        <TextInput
          label="Destination Address"
          placeholder="0x1234567890123456789012345678901234567890"
          value={destinationAddress}
          onChange={(event) => {
            setDestinationAddress(event.currentTarget.value)
          }}
          required
          disabled={locked}
          error={
            destinationAddress.trim() &&
            !isValidEthereumAddress(destinationAddress.trim())
              ? 'Please enter a valid Ethereum address'
              : ''
          }
          description={
            <Text fw={500} size="sm">
              Ethereum address where minted tokens will be sent
            </Text>
          }
        />

        <TextInput
          label="External Transaction ID"
          placeholder="Enter external transaction ID, number, or reference"
          value={externalTx}
          onChange={(event) => {
            setExternalTx(event.currentTarget.value)
          }}
          required
          disabled={locked}
          description={
            <Text fw={500} size="sm">
              External transaction identifier for tracking purposes
            </Text>
          }
        />

        <Select
          label="External Transaction Type"
          placeholder="Select transaction type"
          value={externalType}
          onChange={(value) => {
            setExternalType(value || '')
          }}
          data={[
            { value: 'wire_transfer', label: 'Wire Transfer' },
            { value: 'ach', label: 'ACH' },
            { value: 'sepa', label: 'SEPA' },
            { value: 'cash', label: 'Cash' },
          ]}
          required
          disabled={locked}
          description={
            <Text fw={500} size="sm">
              Type or category of the external transaction
            </Text>
          }
        />

        {inputExecutedAt && (
          <Box>
            {!showTimestampSection ? (
              <InputWrapper
                label="Transaction Executed At"
                required
                description={
                  <Text fw={500} size="sm">
                    Current timestamp will be used unless modified. &nbsp;
                    <Anchor
                      size="sm"
                      onClick={() => {
                        setShowTimestampSection(true)
                      }}
                      data-testid="modify-timestamp-link"
                      style={{ cursor: 'pointer' }}
                    >
                      Modify Transaction Timestamp...
                    </Anchor>
                  </Text>
                }
              ></InputWrapper>
            ) : (
              <Stack gap={0}>
                <TowerDateTimeInput
                  label="Transaction Executed At"
                  value={executedAt}
                  data-testid="executed-at-input"
                  onChange={setExecutedAt}
                  required
                  disabled={locked}
                  description={
                    <Text fw={500} size="sm">
                      Timestamp of the external transaction execution
                    </Text>
                  }
                />
                <Anchor
                  size="sm"
                  onClick={resetToDefaultTimestamp}
                  style={{ cursor: 'pointer' }}
                >
                  Use Default/Current Timestamp
                </Anchor>
              </Stack>
            )}
          </Box>
        )}

        <Group>
          <Stack gap="xs" style={{ width: '100%' }}>
            <Group>
              <Text>Current Price Per Share:</Text>
              <Text size="lg">{usdFormatter(currentPricePerShare)}</Text>
            </Group>

            <Group c="dimmed">
              <Text>Estimated Share Count:</Text>
              <Text>{estimatedShareCount.toFixed(6)}</Text>
            </Group>
          </Stack>
        </Group>

        <Box maw={400}>
          {formError && (
            <Text c="red" size="sm" mt="sm">
              {formError}
            </Text>
          )}

          <TowerApprovalControl
            onApprove={handleInitialSubmit}
            onStartApprove={handleStartApprove}
            onCancel={handleCancelApprove}
            disabled={!isFormValid || createDepositTransaction.isPending}
            buttonText="Save Transaction"
            confirmationText="Slide to confirm mint transaction"
            successText="Mint recorded successfully"
          />
        </Box>
      </Stack>

      <Modal
        opened={showConfirmModal}
        onClose={handleConfirmModalClose}
        title="Confirm Destination Address"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={true}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            To confirm this transaction, please enter the last 4 characters of
            the destination address:
          </Text>

          <Text size="sm" fw={500}>
            Destination: {destinationAddress.slice(0, -4)}****
          </Text>

          <TextInput
            label="Last 4 Characters"
            placeholder="Enter last 4 characters"
            value={confirmationInput}
            onChange={(event) => {
              setConfirmationInput(event.currentTarget.value)
            }}
            maxLength={4}
            size="md"
            autoFocus
            data-autofocus
          />
        </Stack>
      </Modal>
    </TowerCard>
  )
}
